import {
  AMPLITUDE_API_KEY,
  AMPLITUDE_REPLAY_SAMPLE_RATE,
  AMPLITUDE_REPLAY_URL,
  AMPLITUDE_SDK_URL,
  GTM_ID,
  GTM_URL
} from '../constants'

/**
 * Loads the browser analytics tags (GTM, Amplitude) — only after analytics
 * consent (FE-330). The snippets used to sit in index.html and ran before any
 * choice; FE-322 removes them for good once the server-side channels are up.
 */
export interface BrowserTagsConfig {
  gtmId?: string
  amplitudeApiKey?: string
  replaySampleRate?: number
}

let loaded = false

const loadScript = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })

const loadGtm = (gtmId: string) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  return loadScript(`${GTM_URL}?id=${encodeURIComponent(gtmId)}`)
}

const loadAmplitude = async (apiKey: string, replaySampleRate: number) => {
  await loadScript(AMPLITUDE_SDK_URL)
  if (replaySampleRate > 0) {
    await loadScript(AMPLITUDE_REPLAY_URL)
    window.amplitude?.add(
      window.sessionReplay?.plugin({ sampleRate: replaySampleRate })
    )
  }

  window.amplitude?.init(apiKey, {
    serverZone: 'EU',
    autocapture: { elementInteractions: true }
  })
}

/** Idempotent: the tags are injected at most once per page load. */
export const loadBrowserTags = ({
  gtmId = GTM_ID,
  amplitudeApiKey = AMPLITUDE_API_KEY,
  replaySampleRate = AMPLITUDE_REPLAY_SAMPLE_RATE
}: BrowserTagsConfig = {}) => {
  if (loaded) return
  loaded = true

  const tasks = [
    gtmId ? loadGtm(gtmId) : null,
    amplitudeApiKey ? loadAmplitude(amplitudeApiKey, replaySampleRate) : null
  ]

  for (const task of tasks) {
    task?.catch(error => console.error('Browser tag failed to load', error))
  }
}

export const isBrowserTagsLoaded = () => loaded

/** Test hook: forget that the tags were injected. */
export const resetBrowserTags = () => {
  loaded = false
}
